import { createLogger } from '@xanectech/lib';
const logger = createLogger('Data - useUserManagement');
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@xanectech/supabase-client';
import type {
  Database,
  AdminUser,
  UnassignedUserSearchResult,
  UserStats,
} from '@xanectech/types';
import { getErrorMessage } from '@xanectech/lib';
// import { useToast } from '@xanectech/ui/lib';

type AppRole = Database['public']['Enums']['app_role'];
type UserRoleRow = Database['public']['Tables']['user_roles']['Row'];
type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];

interface UserProfileWithCompany extends UserProfileRow {
  companies?: { name: string | null } | null;
}

interface AdminAuthRow {
  email: string | null;
  last_login: string | null;
  user_id: string;
}

function isAppRole(role: string): role is AppRole {
  return (
    role === 'end_user' ||
    role === 'company_admin' ||
    role === 'internal_admin' ||
    role === 'super_admin' ||
    role === 'data_administrator' ||
    role === 'viewer'
  );
}

const buildUserSearchFilter = (query: string) => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) return null;

  const value = `%${trimmedQuery}%`;

  return `first_name.ilike.${value},last_name.ilike.${value},email.ilike.${value}`;
};

export const useUnassignedUsersSearch = ({
  query,
  limit = 10,
  enabled = true,
}: {
  query: string;
  limit?: number;
  enabled?: boolean;
}) => {
  const searchTerm = query?.trim() ?? '';

  return useQuery<UnassignedUserSearchResult[]>({
    queryKey: ['unassigned-users', searchTerm, limit],
    queryFn: async () => {
      const searchFilter = buildUserSearchFilter(searchTerm);

      const supabaseQuery = supabase
        .from('user_profiles')
        .select(
          `
          user_id,
          first_name,
          last_name,
          email,
          role,
          is_active,
          company_id
        `,
        )
        .is('company_id', null)
        .eq('role', 'user')
        .eq('is_active', true)
        .order('first_name', { ascending: true })
        .limit(limit);

      if (searchFilter) {
        supabaseQuery.or(searchFilter);
      }

      const { data, error } = await supabaseQuery;

      if (error) throw error;

      return (data || []) as UnassignedUserSearchResult[];
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    enabled,
  });
};

/**
 * Fetch all users (global admins only - not company users)
 */
export const useAllUsers = () => {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      // Fetch all active users regardless of role
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(
          `
          user_id,
          role,
          is_active,
          created_at
        `,
        )
        .eq('is_active', true);

      if (rolesError) throw rolesError;
      if (!userRoles || userRoles.length === 0) return [];

      // Get unique user IDs
      const userIds = [
        ...new Set((userRoles ?? []).map((role) => role.user_id)),
      ];

      // Fetch user profiles for these users with company names
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select(
          `
          *,
          companies (
            name
          )
        `,
        )
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Get emails from auth.users using the RPC function
      const { data: authData, error: authError } = await supabase.rpc(
        'get_admin_user_data',
        {
          target_user_id: undefined, // Get all users
        },
      );

      if (authError) throw authError;

      // Combine data
      const authRows = (authData as unknown as AdminAuthRow[] | null) ?? [];
      const profileRows =
        (profiles as unknown as UserProfileWithCompany[] | null) ?? [];

      const users: AdminUser[] =
        profileRows.map((profile) => {
          const auth = authRows.find((row) => row.user_id === profile.user_id);
          const userRole = userRoles.find((r) => r.user_id === profile.user_id);
          const companyName = profile.companies?.name;

          return {
            id: profile.id,
            user_id: profile.user_id,
            email: auth?.email || '',
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            full_name: `${profile.first_name || ''} ${
              profile.last_name || ''
            }`.trim(),
            display_name:
              profile.display_name || profile.first_name || auth?.email || '',
            role: profile.role || '',
            primary_role: userRole?.role || profile.role || '',
            status: profile.is_active ? 'active' : 'inactive',
            is_active: profile.is_active,
            company_id: profile.company_id || '',
            company_name: companyName || 'Unknown',
            created_at: profile.created_at,
            updated_at: profile.updated_at,
            last_login_at: auth?.last_login ?? undefined,
            last_login: auth?.last_login ?? undefined,
            user_roles: userRoles.filter((r) => r.user_id === profile.user_id),
          };
        }) || [];

      return users;
    },
  });
};

/**
 * Fetch a single user by ID
 * @param userId
 * @returns user data
 */
export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: async () => {
      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select(
          `
          *,
          companies (
            name
          )
        `,
        )
        .eq('user_id', userId)
        .single();

      if (profileError) throw profileError;
      if (!profile) return null;

      // Fetch user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (rolesError) throw rolesError;

      // Fetch auth data
      const { data: authData, error: authError } = await supabase.rpc(
        'get_admin_user_data',
        {
          target_user_id: userId,
        },
      );

      if (authError) throw authError;

      const authRows = (authData as unknown as AdminAuthRow[] | null) ?? [];
      const auth = authRows.length > 0 ? authRows[0] : null;
      const profileRow = profile as unknown as UserProfileWithCompany;
      const companyName = profileRow.companies?.name;

      const user: AdminUser = {
        id: profile.id,
        user_id: profile.user_id,
        email: auth?.email || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        full_name: `${profile.first_name || ''} ${
          profile.last_name || ''
        }`.trim(),
        display_name:
          profile.display_name || profile.first_name || auth?.email || '',
        role: profile.role || '',
        primary_role: userRoles?.[0]?.role || profile.role || '',
        status: profile.is_active ? 'active' : 'inactive',
        is_active: profile.is_active,
        company_id: profile.company_id || '',
        company_name: companyName || 'Unknown',
      };
      return user;
    },
  });
};

/**
 * Fetch user statistics for global admins
 */
export const useUserStats = () => {
  return useQuery({
    queryKey: ['admin', 'users', 'stats'],
    queryFn: async () => {
      // Get all internal and super admin user IDs
      const { data: adminRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('role', ['internal_admin', 'super_admin'])
        .eq('is_active', true);

      if (rolesError) throw rolesError;

      const adminUserIds = Array.from(
        new Set<string>(
          ((adminRoles ?? []) as Array<{ user_id: string }>).map(
            (r) => r.user_id,
          ),
        ),
      );

      if (adminUserIds.length === 0) {
        return {
          totalUsers: 0,
          total_users: 0,
          activeUsers: 0,
          active_users: 0,
          inactiveUsers: 0,
          suspendedUsers: 0,
          admin_users: 0,
          new_users_this_month: 0,
        };
      }

      // Get profile counts
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, is_active, created_at')
        .in('user_id', adminUserIds);

      if (profilesError) throw profilesError;

      const total = profiles?.length || 0;
      const active = profiles?.filter((p) => p.is_active).length || 0;
      const inactive = total - active;

      // Count new users this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const newThisMonth =
        profiles?.filter(
          (p) =>
            typeof p.created_at === 'string' &&
            new Date(p.created_at as string) >= startOfMonth,
        ).length || 0;

      // Count super admins
      const superAdminCount =
        adminRoles?.filter((r) => r.role === 'super_admin').length || 0;

      const stats: UserStats = {
        totalUsers: total,
        total_users: total,
        activeUsers: active,
        active_users: active,
        inactiveUsers: inactive,
        suspendedUsers: 0,
        admin_users: superAdminCount,
        new_users_this_month: newThisMonth,
      };
      return stats;
    },
  });
};

/**
 * Create user (stub)
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async (userData: Partial<AdminUser>) => {
      // Stub - would use admin API
      logger.info('Create user:', userData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      // toast({
      //   title: 'User Created',
      //   description: 'User created successfully.',
      // });
    },
    onError: (error: unknown) => {
      logger.error('Failed to create user', {
        error: getErrorMessage(error, 'An unexpected error occurred'),
      });
      // toast({
      //   title: 'Create Failed',
      //   description: error.message,
      //   variant: 'destructive',
      // });
    },
  });
};

/**
 * Update user profile
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<AdminUser>;
    }) => {
      // Update user_profiles table
      const profileUpdates: Partial<
        Pick<
          UserProfileRow,
          | 'first_name'
          | 'last_name'
          | 'display_name'
          | 'is_active'
          | 'company_id'
        >
      > = {};

      if (updates.first_name) profileUpdates.first_name = updates.first_name;
      if (updates.last_name) profileUpdates.last_name = updates.last_name;
      if (updates.display_name)
        profileUpdates.display_name = updates.display_name;
      if (updates.is_active !== undefined)
        profileUpdates.is_active = updates.is_active;
      if (updates.company_id !== undefined)
        profileUpdates.company_id = updates.company_id;

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(profileUpdates)
        .eq('user_id', userId);

      if (profileError) throw profileError;

      // Update user_roles table if role is being changed
      if (updates.primary_role) {
        if (!isAppRole(updates.primary_role)) {
          throw new Error(`Invalid role: ${updates.primary_role}`);
        }

        const primaryRole: AppRole = updates.primary_role;

        // First deactivate all existing roles for this user
        const { error: deactivateError } = await supabase
          .from('user_roles')
          .update({ is_active: false })
          .eq('user_id', userId);

        if (deactivateError) throw deactivateError;

        // Check if role already exists
        const { data: existingRole } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', userId)
          .eq('role', primaryRole)
          .single();

        if (existingRole) {
          // Reactivate existing role
          const { error: activateError } = await supabase
            .from('user_roles')
            .update({ is_active: true })
            .eq('id', existingRole.id);

          if (activateError) throw activateError;
        } else {
          // Create new role
          const { error: insertError } = await supabase
            .from('user_roles')
            .insert([
              {
                user_id: userId,
                role: primaryRole,
                is_active: true,
              },
            ]);

          if (insertError) throw insertError;
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      // toast({
      //   title: 'User Updated',
      //   description: 'User updated successfully.',
      // });
    },
    onError: (error: unknown) => {
      logger.error('Failed to update user', { error: getErrorMessage(error) });
      // toast({
      //   title: 'Update Failed',
      //   description: error.message,
      //   variant: 'destructive',
      // });
    },
  });
};

/**
 * Toggle user status (activate/deactivate)
 */
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      userId,
      newStatus,
    }: {
      userId: string;
      newStatus: string;
    }) => {
      const isActive = newStatus === 'active';

      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: isActive })
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'users', 'stats'],
      });
      // toast({
      //   title: 'User Status Updated',
      //   description: 'User status has been successfully updated.',
      // });
    },
    onError: (error: unknown) => {
      logger.error('Failed to update user status', {
        error: getErrorMessage(error),
      });
      // toast({
      //   title: 'Update Failed',
      //   description: error.message,
      //   variant: 'destructive',
      // });
    },
  });
};
